/**
 * The consent gate: no loyalty request carrying the fan's bearer goes out until consent is known.
 *
 * **One decorator, because there is exactly one client.** `new LoyaltyAPI(` appears once in this
 * theme, in ./client.js, and every loyalty request in the bundle goes through the object
 * `getClient()` returns -- `toasts/gate.js`'s enrolment read, `toasts/trigger.js`'s notification
 * poll, all four blocks through `useLoyaltyData`, `useJoinFunnel`, and the library's own `useTiers`.
 * So wrapping that object is a structural guarantee rather than a convention somebody has to
 * remember at each call site, and a new block cannot bypass it by existing.
 *
 * It composes the way the rest of that file does: `withCoalescedFetch` at the fetch level,
 * `withPrefetchCache` around the client, and this outside both. **Outermost matters twice**: a
 * refusal never reaches the prefetch cache, and data fetched while consent was granted cannot be
 * served out of that cache after it is withdrawn.
 *
 * **`getTiers` is exempt, and that is a reading of "every request" rather than a hole in it.**
 * `/api/tiers` is the one public route on the interface: it goes out with no `Authorization` header
 * at all, so it carries no fan identity and there is nothing for a consent gate to protect. Gating
 * it would take the tier ladder away from every signed-out visitor on the join hero -- a regression
 * with no privacy benefit. The principled line is *every method that carries the fan's bearer*.
 *
 * **`joinLoyalty` is exempt because joining is the act of consenting.** `useJoinFunnel` writes
 * consent and then joins inside that write's `.then`, so the join is always preceded by a
 * confirmed record; gating it as well would deadlock the one flow that can produce consent.
 *
 * **A browser with no credential passes through untouched.** Required, not a shortcut: it is what
 * keeps the public tier ladder working, and it is what stops every anonymous page load buying a
 * doomed consent read.
 *
 * The tempting wrong answer, recorded so nobody rediscovers it: resolving `''` from the client's
 * `accessToken` provider instead. That produces exactly the right ROUTING for free -- public reads
 * through, self-scoped reads refused before the wire -- and exactly the wrong MESSAGE, because the
 * library renders a dead credential as "Session expired — please log in again". A fan who has not
 * consented has a perfectly good session.
 *
 * **IT RETURNS A PROXY, AND THAT IS LOAD-BEARING RATHER THAN STYLISTIC.** This used to build a
 * plain `Object.create(null)` and copy across everything `methodNames()` found -- which uses
 * `Object.getOwnPropertyNames`, and that **does not enumerate symbols**. The object it handed back
 * therefore had no `PREFETCH_CACHE`, the `Symbol('loyalty.prefetchCache')` that `withPrefetchCache`
 * exposes as its only external control. Two things followed, and neither said anything in a log:
 *
 * 1. `toasts/trigger.js`'s `client[ PREFETCH_CACHE ]?.invalidate( 'getBadgesDetail' )` -- which its
 *    own comment calls mandatory, not hygiene -- was an optional call on `undefined`, so every
 *    reconcile inside the cache's 30s window diffed the same settled overview and the badge branch
 *    could never fire. Exactly the bug uxlib 0.7.0/0.8.0 had just fixed, reintroduced here.
 * 2. That cache is keyed by METHOD NAME with no fan in the key, and with the symbol gone it could
 *    be neither invalidated nor cleared from outside. So a second fan becoming current inside one
 *    document within 30s was served the first fan's challenges, badges, leaderboard and profile
 *    stats. ./client.js reasons about "a sign-in as somebody else" for the token PROVIDER, one
 *    layer below the cache, which is why this went unnoticed.
 *
 * A proxy forwards everything it does not deliberately intercept -- symbols included -- which is
 * the same reason `withPrefetchCache` is one. Wrapping a decorator in a shape that has to enumerate
 * the thing it wraps is the mistake; do not go back to copying.
 */

import { LoyaltyConsentRequiredError } from './consent/errors.js';

/**
 * Methods that must never be gated.
 *
 * An explicit list, which is this codebase's rule -- `vite.config.ts`'s entry map says "explicit,
 * not a glob" and `loyalty.jsx`'s block map matches on equality rather than prefix for the same
 * reason. A pattern here would silently exempt the next method whose name happened to match.
 */
export const CONSENT_EXEMPT = [ 'getTiers', 'joinLoyalty' ];

/**
 * Every callable this object offers, own and inherited.
 *
 * The prototype chain is walked because `LoyaltyAPI` is a class, so its methods are not own
 * properties -- and because `withPrefetchCache` returns a proxy over one, this has to cope with
 * both shapes. Object.prototype is excluded so `toString` and friends are left alone.
 *
 * `withConsentGate` no longer calls this -- a proxy needs no list of what to wrap, which is the
 * whole point of the change recorded above. It is kept, and exported, because `consentGate.test.js`
 * uses it to assert that every method of the REAL installed interface is reachable through the
 * gate: that test is what makes a library upgrade adding an ungated route fail here rather than
 * ship. Reading it as dead code and deleting it would take that guarantee with it.
 *
 * @param {Object} target The client.
 * @return {string[]} Method names, deduplicated.
 */
export function methodNames( target ) {
	const names = new Set();

	for (
		let node = target;
		node && node !== Object.prototype;
		node = Object.getPrototypeOf( node )
	) {
		Object.getOwnPropertyNames( node ).forEach( name => {
			if ( name === 'constructor' ) {
				return;
			}

			if ( typeof target[ name ] === 'function' ) {
				names.add( name );
			}
		} );
	}

	return [ ...names ];
}

/**
 * Wrap a loyalty client so its fan-scoped methods wait for, and respect, loyalty consent.
 *
 * @param {Object}                 client                Anything ILoyaltyAPI-shaped.
 * @param {Object}                 options               Inputs.
 * @param {() => Promise<boolean>} options.isGranted     Resolves the consent state. Must never reject.
 * @param {() => boolean}          options.hasCredential Whether the fan has a live credential.
 * @param {string[]}               [options.exempt]      Methods to leave alone. Defaults to CONSENT_EXEMPT.
 * @return {Object} A same-shaped client.
 */
export function withConsentGate( client, { isGranted, hasCredential, exempt } ) {
	const exemptions = exempt || CONSENT_EXEMPT;

	// Wrappers are memoised so a method read twice is the same function both times. Nothing in the
	// tree compares method identities today, but `useLoyaltyData` keys its effect on the CLIENT
	// object and a decorator that minted a fresh closure per property read is the kind of thing
	// that turns a future memo into a render loop.
	const wrappers = new Map();

	return new Proxy( client, {
		get( target, prop, receiver ) {
			const value = Reflect.get( target, prop, receiver );

			// Symbols forward untouched. PREFETCH_CACHE is one, and dropping it is the defect the
			// module docblock describes. Non-callables forward too: `baseUrl` and friends are data.
			if ( typeof prop !== 'string' || typeof value !== 'function' ) {
				return value;
			}

			// Never hand back a gated constructor -- `methodNames()` skips it for the same reason.
			if ( prop === 'constructor' ) {
				return value;
			}

			if ( exemptions.indexOf( prop ) !== -1 ) {
				// Bound rather than wrapped: an exempt method has nothing to wait for, and binding
				// keeps `this` pointing at the inner client through the proxy.
				return value.bind( target );
			}

			if ( ! wrappers.has( prop ) ) {
				wrappers.set( prop, ( ...args ) => {
					// No credential is not a consent question. The library already refuses
					// self-scoped routes before the wire in that state, with the right message,
					// and the public ones must still succeed.
					if ( ! hasCredential() ) {
						return client[ prop ]( ...args );
					}

					return isGranted().then( granted => {
						if ( ! granted ) {
							// Before the wire. Not a rejected fetch and not an empty result: a
							// caller reading a log has to be able to tell "refused for consent"
							// from "the gateway is down", and the blocks must not render a session
							// error for it.
							return Promise.reject( new LoyaltyConsentRequiredError( prop ) );
						}

						return client[ prop ]( ...args );
					} );
				} );
			}

			return wrappers.get( prop );
		},
	} );
}
